import * as Sentry from "@sentry/nextjs";
import type { BrowserOptions, EdgeOptions, NodeOptions } from "@sentry/nextjs";

export const getSentryDsn = () =>
  process.env.SENTRY_DSN ?? process.env.NEXT_PUBLIC_SENTRY_DSN;

export const getSentryEnvironment = () =>
  process.env.SENTRY_ENVIRONMENT ??
  process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT ??
  process.env.NODE_ENV ??
  "development";

export const getSentryRelease = () =>
  process.env.SENTRY_RELEASE ?? process.env.NEXT_PUBLIC_SENTRY_RELEASE;

const eventTextCandidates = (event: Sentry.ErrorEvent) => [
  event.message,
  event.logentry?.message,
  ...(event.exception?.values?.map((value) => value.value) ?? []),
];

const isUpstreamImageFailure = (event: Sentry.ErrorEvent): boolean =>
  eventTextCandidates(event).some(
    (text) =>
      typeof text === "string" &&
      text.includes("upstream image response failed"),
  );

const isRateLimitFailure = (event: Sentry.ErrorEvent): boolean =>
  eventTextCandidates(event).some(
    (text) => typeof text === "string" && text.includes("status code 429"),
  );

export const getBaseSentryOptions = (): Partial<
  BrowserOptions & NodeOptions & EdgeOptions
> => ({
  dsn: getSentryDsn(),
  environment: getSentryEnvironment(),
  release: getSentryRelease(),
  tracesSampleRate: process.env.NODE_ENV === "production" ? 0.01 : 0,
  enableLogs: true,
  integrations: [
    Sentry.consoleLoggingIntegration({
      levels: ["warn", "error"],
    }),
    Sentry.captureConsoleIntegration({
      levels: ["error"],
    }),
  ],
  beforeSend(event) {
    if (isUpstreamImageFailure(event) || isRateLimitFailure(event)) {
      return null;
    }
    return event;
  },
});
